import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Trophy, Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";

const SORTS = { points: "Points", deals_won: "Deals won" };
const MEDALS = ["text-yellow-500", "text-slate-400", "text-amber-700"];
const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const sortRows = (rows, by) => [...rows].sort((a, b) => by === "deals_won"
  ? (b.deals_won - a.deals_won) || (b.proposals_sent - a.proposals_sent) || (b.views - a.views) || (b.scans - a.scans)
  : (b.points - a.points) || (b.deals_won - a.deals_won));

export const RepLeaderboardTab = () => {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [sortBy, setSortBy] = useState("points");

  useEffect(() => {
    setData(null);
    api.get(`/api/webadvisor/leaderboard?week_offset=${offset}`).then(r => setData(r.data)).catch(() => setData({ reps: [] }));
  }, [offset]);

  const rows = data ? sortRows(data.reps, sortBy) : [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="rep-leaderboard-tab">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-500" />Rep Leaderboard</h2>
          {data?.points && <p className="text-xs text-slate-500">Points: scan {data.points.scan} · proposal sent {data.points.sent} · prospect view {data.points.view} · deal won {data.points.won}</p>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex bg-slate-100 rounded-lg p-1" data-testid="leaderboard-sort">
            {Object.entries(SORTS).map(([k, label]) => (
              <button key={k} onClick={() => setSortBy(k)} className={`text-xs font-medium px-3 py-1.5 rounded-md transition-colors ${sortBy === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid={`leaderboard-sort-${k}`}>{label}</button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setOffset(o => o - 1)} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="leaderboard-prev-week"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium text-slate-700 min-w-[140px] text-center" data-testid="leaderboard-week-label">
              {data?.week_start ? `${offset === 0 ? "This week · " : ""}${fmt(data.week_start)} – ${fmt(data.week_end)}` : "…"}
            </span>
            <button onClick={() => setOffset(o => Math.min(0, o + 1))} disabled={offset === 0} className="p-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30" data-testid="leaderboard-next-week"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
      {!data ? <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead className="w-12">#</TableHead><TableHead>Rep</TableHead><TableHead className="text-right">Scans</TableHead><TableHead className="text-right">Proposals sent</TableHead><TableHead className="text-right">Views</TableHead><TableHead className="text-right">Deals won</TableHead><TableHead className="text-right">Points</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={r.rep_id} data-testid={`leaderboard-row-${r.rep_id}`}>
                  <TableCell className="font-black">{i < 3 && (r.points > 0 || r.deals_won > 0) ? <Trophy className={`w-4 h-4 ${MEDALS[i]}`} /> : i + 1}</TableCell>
                  <TableCell><p className="font-semibold text-slate-900">{r.name}</p><p className="text-xs text-slate-400">{r.email}</p></TableCell>
                  <TableCell className="text-right">{r.scans}</TableCell>
                  <TableCell className="text-right">{r.proposals_sent}</TableCell>
                  <TableCell className="text-right">{r.views}</TableCell>
                  <TableCell className={`text-right ${sortBy === "deals_won" ? "font-black text-green-700" : ""}`}>{r.deals_won}</TableCell>
                  <TableCell className={`text-right ${sortBy === "points" ? "font-black text-blue-700" : ""}`} data-testid={`leaderboard-points-${r.rep_id}`}>{r.points}</TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8" data-testid="leaderboard-empty">No sales reps yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
